import { CognitoUser } from 'amazon-cognito-identity-js';
import { FreeUser, PaidUser } from './index';
import { Role, Language } from '@tendium/prom-types';
import { ICompany } from '../company/types';
import { IUserPreferences } from '../users/Preferences/types';
import { IApiUser, MfaMode } from '../users/types';
import { SetStateAction } from 'react';
import { COUNTRIES } from 'src/helpers/constants';
import { isObject } from 'src/helpers';
import { RequestSettings } from '../users/RequestSettings/types';

export type CountryCode = (typeof COUNTRIES)[number]['key'];
export const DEFAULT_COUNTRY: CountryCode = 'SE';
export type ChallengeName =
  | 'CUSTOM_CHALLENGE'
  | 'MFA_SETUP'
  | 'NEW_PASSWORD_REQUIRED'
  | 'SELECT_MFA_TYPE'
  | 'SMS_MFA'
  | 'SOFTWARE_TOKEN_MFA';
export interface CognitoAttributes {
  email: string;
  sub: string;
  email_verified: boolean;
  address?: string;
  phone_number?: string;
  given_name?: string;
  family_name?: string;
  locale?: Language;
  'custom:userType'?: UserType;
  'custom:country'?: CountryCode;
  'custom:orgId'?: string;
  'custom:orgName'?: string;
}
export interface AuthUser extends CognitoUser {
  username?: string;
  attributes: CognitoAttributes;
  challengeName?: ChallengeName;
  // types from cognito lib, will be removed when upgrade amplify to next major version
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  challengeParam?: any;
}
export type FreeUserAttributes = {
  email: string;
  address?: string;
  phone?: string;
  name?: string;
  lastName?: string;
  language?: Language;
  country?: CountryCode;
  orgNumber?: string;
  orgName?: string;
};
export interface ApiMeUserFields {
  paymentPlan: Role;
  role: Role;
  company: ICompany;
  isAdmin: boolean;
  language: Language | null;
  featureFlags: string[];
  roles: Role[];
  requestSettings: RequestSettings[];
  settings?: IUserPreferences;
  mfaMode?: MfaMode;
}
export type ApiMeUser = IApiUser & ApiMeUserFields;
export type AppData = {
  loading: boolean;
  authUser?: AuthUser | null;
  user?: PaidUser | FreeUser;
  error?: Error;
  email?: string;
  permission?: UserType;
  password?: string;
};
export type AppCtxData = AppData & {
  setResult: (value: SetStateAction<AppData>) => void;
  handleFreeUser: (data: AuthUser) => void;
  handlePaidUser: (data: AuthUser) => Promise<void>;
};
export enum UserType {
  Paid = 'PAID',
  Free = 'FREE'
}
export enum AmplifyError {
  IllegalOrigin = 'IllegalOriginError',
  DisabledUser = 'disabledUserError'
}
export interface IErrorMessage {
  errorMessage: string;
  option: string | null;
}
export enum AuthQueryParams {
  MsRedirectUri = 'redirect_uri',
  MsState = 'state',
  AswError = 'error_description'
}
export interface IAwsError {
  [AuthQueryParams.AswError]: string;
}
export function isAwsError(u: unknown): u is IAwsError {
  return isObject(u) && AuthQueryParams.AswError in u && typeof u[AuthQueryParams.AswError] === 'string';
}
export function isPaidUser(u: unknown): u is PaidUser {
  return isObject(u) && 'permission' in u && u.permission === UserType.Paid;
}
export function isFreeUser(u: unknown): u is FreeUser {
  return isObject(u) && 'permission' in u && u.permission === UserType.Free;
}

export type UseSignUpCtx = [SignUpFlow, (step: SignUpFlow) => void];
export enum SignUpFlow {
  CompanyInfo = 'CompanyInfo',
  UserInfo = 'UserInfo'
}
